import React, { useState, useEffect } from "react";
import {
  X,
  Download,
  Loader,
  AlertCircle,
  FileText,
  Image,
  Table,
  FileSpreadsheet,
} from "lucide-react";

const FileViewerModal = ({ open, onClose, file, viewFile }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [csvData, setCsvData] = useState(null);

  useEffect(() => {
    if (open && file) {
      loadFile();
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, file]);

  // CSV를 HTML 테이블로 변환
  const csvToHtml = (text) => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return "";

    // CSV 파싱 개선
    const parseCSVLine = (line) => {
      const result = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const rows = lines.slice(1).map((line) => parseCSVLine(line));

    let html = `
      <style>
        .csv-table {
          border-collapse: collapse;
          width: 100%;
          font-family: Arial, sans-serif;
        }
        .csv-table th {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 8px 12px;
          text-align: left;
          font-weight: bold;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .csv-table td {
          border: 1px solid #dee2e6;
          padding: 8px 12px;
        }
        .csv-table tr:nth-child(even) {
          background-color: #f8f9fa;
        }
        .csv-table tr:hover {
          background-color: #e9ecef;
        }
      </style>
      <table class="csv-table">
        <thead>
          <tr>
            ${headers.map((h) => `<th>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) => `
            <tr>
              ${row.map((cell) => `<td>${cell}</td>`).join("")}
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
    `;

    return html;
  };

  const loadFile = async () => {
    setLoading(true);
    setError(false);

    try {
      const blob = await viewFile(file.id);
      if (!blob) {
        throw new Error("파일을 불러올 수 없습니다.");
      }

      const ext = (file.file_type || "").toLowerCase().replace(".", "");

      // MIME 타입 설정
      const mimeTypes = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        bmp: "image/bmp",
        svg: "image/svg+xml",
        webp: "image/webp",
        txt: "text/plain",
        md: "text/markdown",
        log: "text/plain",
        json: "application/json",
        xml: "application/xml",
        csv: "text/csv; charset=utf-8",
        html: "text/html; charset=utf-8",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ppt: "application/vnd.ms-powerpoint",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      };

      const mimeType = mimeTypes[ext] || "application/octet-stream";
      const typedBlob = new Blob([blob], { type: mimeType });
      const url = URL.createObjectURL(typedBlob);
      setBlobUrl(url);

      // CSV 파일 처리
      if (ext === "csv") {
        const text = await blob.text();
        const html = csvToHtml(text);
        setHtmlContent(html);
      }
      // 텍스트 파일 처리
      else if (["txt", "md", "log", "json", "xml"].includes(ext)) {
        const text = await blob.text();
        setHtmlContent(
          `<pre style="padding: 20px; font-family: monospace; white-space: pre-wrap;">${text}</pre>`
        );
      }
      // Excel 파일 처리 (SheetJS 사용)
      else if (ext === "xlsx" || ext === "xls") {
        if (!window.XLSX) {
          const script = document.createElement("script");
          script.src =
            "https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js";
          script.onload = async () => {
            const arrayBuffer = await blob.arrayBuffer();
            const workbook = window.XLSX.read(arrayBuffer, { type: "array" });

            // 첫 번째 시트를 HTML로 변환
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const html = window.XLSX.utils.sheet_to_html(worksheet, {
              header: `
                <style>
                  table { border-collapse: collapse; width: 100%; }
                  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                  th { background-color: #f2f2f2; font-weight: bold; }
                  tr:nth-child(even) { background-color: #f9f9f9; }
                </style>
              `,
              editable: false,
            });

            // 여러 시트가 있는 경우 탭 추가
            if (workbook.SheetNames.length > 1) {
              let fullHtml = '<div style="padding: 10px;">';
              fullHtml += '<div style="margin-bottom: 10px;">';
              workbook.SheetNames.forEach((name, idx) => {
                fullHtml += `<button onclick="showSheet(${idx})" style="margin-right: 5px; padding: 5px 10px; ${
                  idx === 0 ? "background: #007bff; color: white;" : ""
                }">${name}</button>`;
              });
              fullHtml += "</div>";
              fullHtml += '<div id="sheet-content">' + html + "</div>";
              fullHtml +=
                "<script>function showSheet(idx) { /* 시트 전환 로직 */ }</script>";
              fullHtml += "</div>";
              setHtmlContent(fullHtml);
            } else {
              setHtmlContent(html);
            }
          };
          document.head.appendChild(script);
        }
      }
      // Word 문서 처리 (mammoth.js 사용)
      else if (ext === "docx") {
        if (!window.mammoth) {
          const script = document.createElement("script");
          script.src =
            "https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.min.js";
          script.onload = async () => {
            const arrayBuffer = await blob.arrayBuffer();
            const result = await window.mammoth.convertToHtml({ arrayBuffer });
            setHtmlContent(`
              <div style="padding: 20px; max-width: 800px; margin: 0 auto;">
                <style>
                  img { max-width: 100%; height: auto; }
                  p { margin: 10px 0; }
                  h1, h2, h3, h4, h5, h6 { margin: 15px 0 10px 0; }
                </style>
                ${result.value}
              </div>
            `);
          };
          document.head.appendChild(script);
        }
      }
      // PowerPoint 처리 (pptx2html 또는 대안)
      else if (ext === "pptx") {
        // PPT는 복잡하므로 기본 안내 표시
        setHtmlContent(`
          <div style="text-align: center; padding: 50px;">
            <h2>PowerPoint 프레젠테이션</h2>
            <p>브라우저에서 PPT 미리보기는 제한적입니다.</p>
            <p>파일을 다운로드하여 확인하시거나, Google Slides로 열어보세요.</p>
          </div>
        `);
      }
    } catch (err) {
      setError(true);
      console.error("파일 로드 오류:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (blobUrl) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.file_name;
      link.click();
    } else {
      try {
        const blob = await viewFile(file.id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.file_name;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("다운로드 실패:", err);
      }
    }
  };

  if (!open || !file) return null;

  const ext = (file.file_type || "").toLowerCase().replace(".", "");
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(
    ext
  );
  const isPdf = ext === "pdf";
  const hasHtmlPreview = [
    "csv",
    "txt",
    "md",
    "log",
    "json",
    "xml",
    "xlsx",
    "xls",
    "docx",
  ].includes(ext);

  // 미리보기 불가능한 파일
  const isArchive = ["zip", "rar", "7z", "tar", "gz"].includes(ext);
  const isMedia = ["mp4", "avi", "mov", "wmv", "mp3", "wav", "flac"].includes(
    ext
  );
  const isHwp = ["hwp", "hwpx"].includes(ext);
  const isPpt = ["ppt", "pptx"].includes(ext);

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[1200px] h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {ext === "xlsx" || ext === "xls" ? (
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
            ) : ext === "csv" ? (
              <Table className="w-5 h-5 text-green-600" />
            ) : (
              <FileText className="w-5 h-5 text-gray-600" />
            )}
            <h2 className="text-lg font-medium text-gray-900">
              {file.file_name}
            </h2>
            <span className="text-sm text-gray-500">
              ({formatFileSize(file.file_size)})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Download className="w-4 h-4" />
              다운로드
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader className="w-8 h-8 text-gray-400 animate-spin" />
              <p className="mt-4 text-gray-500">파일을 불러오는 중...</p>
              {(ext === "xlsx" || ext === "xls" || ext === "docx") && (
                <p className="mt-2 text-sm text-gray-400">
                  Office 파일 변환 중...
                </p>
              )}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full">
              <AlertCircle className="w-12 h-12 text-red-400" />
              <p className="mt-4 text-gray-700 font-medium">
                파일을 불러올 수 없습니다
              </p>
              <p className="mt-2 text-sm text-gray-500">
                파일이 손상되었거나 지원하지 않는 형식일 수 있습니다.
              </p>
              <button
                onClick={handleDownload}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                다운로드 시도
              </button>
            </div>
          ) : (
            <div className="h-full">
              {/* 이미지 미리보기 */}
              {isImage && blobUrl && (
                <div className="flex items-center justify-center h-full p-8 bg-gray-100">
                  <img
                    src={blobUrl}
                    alt={file.file_name}
                    className="max-w-full max-h-full object-contain shadow-lg rounded"
                  />
                </div>
              )}

              {/* PDF 미리보기 */}
              {isPdf && blobUrl && (
                <embed
                  src={blobUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  title={file.file_name}
                />
              )}

              {/* HTML 렌더링 미리보기 (CSV, Excel, Word 등) */}
              {hasHtmlPreview && htmlContent && (
                <div className="h-full bg-white overflow-auto">
                  {ext === "csv" && (
                    <div className="bg-green-50 border-b border-green-200 p-3 sticky top-0 z-10">
                      <div className="flex items-center gap-2">
                        <Table className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-800 font-medium">
                          CSV 스프레드시트 미리보기
                        </span>
                      </div>
                    </div>
                  )}
                  {(ext === "xlsx" || ext === "xls") && (
                    <div className="bg-blue-50 border-b border-blue-200 p-3 sticky top-0 z-10">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800 font-medium">
                          Excel 스프레드시트 미리보기
                        </span>
                      </div>
                    </div>
                  )}
                  {ext === "docx" && (
                    <div className="bg-indigo-50 border-b border-indigo-200 p-3 sticky top-0 z-10">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm text-indigo-800 font-medium">
                          Word 문서 미리보기
                        </span>
                      </div>
                    </div>
                  )}
                  <div
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    className="p-4"
                  />
                </div>
              )}

              {/* 미리보기 불가능한 파일들 */}
              {!isImage && !isPdf && !hasHtmlPreview && (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                  <div className="text-6xl mb-6">
                    {isArchive && "📦"}
                    {isMedia &&
                      (["mp3", "wav", "flac"].includes(ext) ? "🎵" : "🎬")}
                    {isHwp && "📄"}
                    {isPpt && "📊"}
                    {!isArchive && !isMedia && !isHwp && !isPpt && "📎"}
                  </div>
                  <p className="text-xl text-gray-800 font-semibold mb-2">
                    {isHwp
                      ? "한글(HWP) 문서"
                      : isPpt
                      ? "PowerPoint 프레젠테이션"
                      : isArchive
                      ? "압축 파일"
                      : isMedia
                      ? "미디어 파일"
                      : "파일"}
                  </p>
                  <p className="text-lg text-gray-700 font-medium">
                    브라우저에서 미리보기를 지원하지 않습니다
                  </p>
                  <p className="mt-2 text-sm text-gray-500 max-w-md text-center">
                    {isHwp &&
                      "한글 문서는 한컴오피스 프로그램에서만 열 수 있습니다."}
                    {isPpt &&
                      "PowerPoint 파일은 다운로드 후 확인하거나 Google Slides에서 열어보세요."}
                    {isArchive &&
                      "압축 파일의 내용을 확인하려면 다운로드 후 압축을 해제해주세요."}
                    {isMedia &&
                      "미디어 파일을 재생하려면 다운로드 후 미디어 플레이어로 열어주세요."}
                  </p>
                  <button
                    onClick={handleDownload}
                    className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    {file.file_name} 다운로드
                  </button>
                  <p className="mt-4 text-xs text-gray-400">
                    파일 크기: {formatFileSize(file.file_size)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;
